import React from 'react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

interface DownloadTableXlsxProps {
  filename: string;
  sheetName: string;
  tableRef: React.RefObject<HTMLTableElement>;
  children: React.ReactNode;
}

const DownloadTableXlsx: React.FC<DownloadTableXlsxProps> = ({ filename, sheetName, tableRef, children }) => {
  const downloadXlsx = async () => {
    if (!tableRef.current) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(sheetName);
    const rowsArray = Array.from(tableRef.current.rows);

    // Agregar los datos de la tabla
    rowsArray.forEach(row => {
      const rowData = Array.from(row.cells).map(cell => cell.innerText);
      worksheet.addRow(rowData);
    });

    // Ajustar el ancho de las columnas
    worksheet.columns.forEach((column, index) => {
      let maxLength = 0;
      rowsArray.forEach(row => {
        const cellValue = row.cells[index]?.innerText || '';
        maxLength = Math.max(maxLength, cellValue.length);
      });
      column.width = maxLength + 2;
    });

    // Aplicar estilos y añadir imágenes
    for (let i = 0; i < rowsArray.length; i++) {
      const cells = rowsArray[i].cells;
      for (let j = 0; j < cells.length; j++) {
        const cell = worksheet.getCell(i + 1, j + 1);
        const computedStyle = window.getComputedStyle(cells[j]);

        // Aplicar estilo de fuente y bordes
        cell.font = {
          name: computedStyle.fontFamily,
          size: 10,
          bold: computedStyle.fontWeight === 'bold' || parseInt(computedStyle.fontWeight) >= 700,
          italic: computedStyle.fontStyle === 'italic',
          underline: computedStyle.textDecoration.includes('underline') as boolean,
          color: { argb: rgbToArgb(computedStyle.color) }
        };

        const bgColor = rgbToArgb(computedStyle.backgroundColor);
        const finalBgColor = bgColor === 'FF000000' ? 'FFFFFFFF' : bgColor;
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: finalBgColor }
        };

        cell.border = {
          top: { style: 'thin', color: { argb: rgbToArgb(computedStyle.borderTopColor) } },
          left: { style: 'thin', color: { argb: rgbToArgb(computedStyle.borderLeftColor) } },
          bottom: { style: 'thin', color: { argb: rgbToArgb(computedStyle.borderBottomColor) } },
          right: { style: 'thin', color: { argb: rgbToArgb(computedStyle.borderRightColor) } }
        };

        cell.alignment = {
          vertical: computedStyle.verticalAlign as 'top' | 'middle' | 'bottom',
          horizontal: computedStyle.textAlign as 'left' | 'center' | 'right',
          wrapText: true
        };

        // Añadir imágenes
        const images = cells[j].getElementsByTagName('img');
        if (images.length > 0) {
          try {
            const img = images[0];
            const imgHeight = img.height || 24;
            const naturalWidth = img.naturalWidth || 1;
            const naturalHeight = img.naturalHeight || 1;
            const aspectRatio = naturalWidth / naturalHeight;
            const imgWidth = imgHeight * aspectRatio;
            const response = await fetch(img.src, {
              method: 'GET',
              headers: {
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                'Pragma': 'no-cache'
              }
            });
            if (!response.ok) {
              throw new Error(`Image request failed (${response.status})`);
            }

            const arrayBuffer = await response.arrayBuffer();
            const imageId = workbook.addImage({
              buffer: new Uint8Array(arrayBuffer),
              extension: 'png',
            });

            worksheet.getRow(i + 1).height = imgHeight * 0.75;
            worksheet.addImage(imageId, {
              tl: { col: j, row: i },
              ext: { width: imgWidth, height: imgHeight },
              editAs: 'oneCell',
            });
          } catch (error) {
            // Ignore image errors (e.g., CORS) and continue generating the file.
            console.warn('DownloadTableXlsx: image skipped', {
              src: images[0]?.src,
              error,
            });
          }
        }
      }
    }

    // Generar el archivo Excel
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `${filename}.xlsx`);
  };

  const rgbToArgb = (rgb: string) => {
    const match = rgb.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
    if (!match) return 'FF000000';
    const r = parseInt(match[1]).toString(16).padStart(2, '0');
    const g = parseInt(match[2]).toString(16).padStart(2, '0');
    const b = parseInt(match[3]).toString(16).padStart(2, '0');
    return `FF${r}${g}${b}`;
  };

  return (
    <div onClick={downloadXlsx}>
      {children}
    </div>
  );
};

export default DownloadTableXlsx;
